"use client";
import React, { useEffect, useState, useMemo } from "react";
import { Mail, Phone, MapPin, Instagram, Facebook, Send } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ICMS, ISettings } from "@/store/home/homeModel";
import { useAppDispatch } from "@/hooks/hooks";
import { createLookupObject } from "@/lib/helper";
import { insertInquiry } from "@/store/home/home.services";
import Banner from "@/components/Banner";
import { useRouter } from "next/navigation";
import { ErrorToast } from "@/components/common/Toast";

interface ContactProps {
  cmsData: ICMS[];
  bannerData: any[];
  settingData: ISettings[];
}

export function Contact({ cmsData, bannerData, settingData }: ContactProps) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const lookUpCMSData = useMemo(() => {
    return createLookupObject(cmsData || [], "Name");
  }, [cmsData]);

  const banner =
    bannerData?.filter((banner: any) => banner?.BannerType === "contact-us") ||
    [];

  const Settings: Record<string, ISettings> = settingData?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );

  const LocationMap = Settings?.["LocationMap"]?.SettingValue;
  const instagram = Settings?.["instagram"]?.SettingValue;
  const facebook = Settings?.["facebook"]?.SettingValue;
  const addressmap = Settings?.["addressmap"]?.SettingValue;
  const address = Settings?.["address"]?.SettingValue;
  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue;
  const email = Settings?.["email"]?.SettingValue;

  const formik: any = useFormik({
    initialValues: {
      FirstName: "",
      LastName: "",
      Email: "",
      MobileNo: "",
      Message: "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      FirstName: Yup.string().trim().required("First Name is required"),
      LastName: Yup.string().trim().required("Last Name is required"),
      MobileNo: Yup.string()
        .required("Mobile number is required")
        .matches(/^[0-9]{10}$/, "Mobile number is not valid"),
      Email: Yup.string()
        .required("Email is required")
        .test("is-email-required", "Email is not valid", function (value) {
          if (!value) return true;
          return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
        }),
    }),
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        FirstName: values?.FirstName || "",
        LastName: values?.LastName || "",
        Email: values?.Email || "",
        MobileNo: `${values?.MobileNo}`,
        Remarks: values?.Message || "",
      };
      try {
        const response = await dispatch(insertInquiry(payload));
        if (response.meta.requestStatus === "fulfilled") {
          resetForm();
          router.push("/thankyou/");
        } else {
          ErrorToast("Error sending message!");
        }
      } catch (error) {
        console.log("error", error);
      }
    },
  });

  return (
    <>
      {/* Hero Section */}
      <Banner BannerList={banner} />

      {/* Contact Cards */}
      <section className="py-20 bg-gradient-to-b from-white to-[#F3EBDD]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow border border-[#D8C4A8]/30">
              <div className="w-16 h-16 bg-gradient-to-br from-[#3E6B4E] to-[#1F3D2B] rounded-2xl flex items-center justify-center mb-6">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3
                className="text-2xl mb-3 text-[#5A3A1E]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Call Us
              </h3>
              <p className="text-[#5A3A1E]/85 mb-4">
                Available 24/7 for your assistance
              </p>
              <a
                href={`tel:${PhoneNo || "+91 9876543210"}`}
                className="text-[#BF5500] hover:text-[#7A4A1D] transition-colors text-lg font-medium"
              >
                {PhoneNo || "+91 9876543210"}
              </a>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow border border-[#D8C4A8]/30">
              <div className="w-16 h-16 bg-gradient-to-br from-[#3E6B4E] to-[#1F3D2B] rounded-2xl flex items-center justify-center mb-6">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3
                className="text-2xl mb-3 text-[#5A3A1E]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Email Us
              </h3>
              <p className="text-[#5A3A1E]/85 mb-4">
                We'll respond within 24 hours
              </p>
              <a
                href={`mailto:${email || "hello@aranya.com"}`}
                className="text-[#BF5500] hover:text-[#7A4A1D] transition-colors text-lg font-medium"
              >
                {email || "hello@aranya.com"}
              </a>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-shadow border border-[#D8C4A8]/30">
              <div className="w-16 h-16 bg-gradient-to-br from-[#3E6B4E] to-[#1F3D2B] rounded-2xl flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3
                className="text-2xl mb-3 text-[#5A3A1E]"
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                Visit Us
              </h3>
              <p className="text-[#5A3A1E]/85 mb-4">Near Gir National Park</p>
              <a
                href={addressmap || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className={`${addressmap ? "text-[#BF5500] text-lg font-medium" : ""}`}
              >
                {address || "Near Gir National Park, Sasan Gir, Gujarat"}
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-[#5A3A1E] mb-6">Send Us a Message</h2>
              <form
                onSubmit={(e) => formik.handleSubmit(e)}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="FirstName"
                      className="block text-[#5A3A1E] mb-2 font-medium"
                    >
                      First Name*
                    </label>
                    <input
                      name="FirstName"
                      type="text"
                      placeholder="Enter Your First Name"
                      className={`w-full px-6 py-4 bg-[#F3EBDD]/30 border border-[#D8C4A8] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#BF5500] transition-all ${formik.errors.FirstName && formik.touched.FirstName ? "border-red-500" : ""}`}
                      maxLength={50}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values?.FirstName}
                    />
                    {formik.touched.FirstName && formik.errors.FirstName && (
                      <p className="text-red-500 text-sm mt-1">
                        {formik.errors.FirstName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="LastName"
                      className="block text-[#5A3A1E] mb-2 font-medium"
                    >
                      Last Name*
                    </label>
                    <input
                      name="LastName"
                      type="text"
                      placeholder="Enter Your Last Name"
                      className={`w-full px-6 py-4 bg-[#F3EBDD]/30 border border-[#D8C4A8] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#BF5500] transition-all ${formik.errors.LastName && formik.touched.LastName ? "border-red-500" : ""}`}
                      maxLength={50}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values?.LastName}
                    />
                    {formik.touched.LastName && formik.errors.LastName && (
                      <p className="text-red-500 text-sm mt-1">
                        {formik.errors.LastName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="Email"
                      className="block text-[#5A3A1E] mb-2 font-medium"
                    >
                      Email*
                    </label>
                    <input
                      id="Email"
                      name="Email"
                      type="email"
                      placeholder="Enter Your Email"
                      className={`w-full px-6 py-4 bg-[#F3EBDD]/30 border border-[#D8C4A8] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#BF5500] transition-all ${formik.errors.Email && formik.touched.Email ? "border-red-500" : ""}`}
                      maxLength={50}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values?.Email}
                    />
                    {formik.touched.Email && formik.errors.Email && (
                      <p className="text-red-500 text-sm mt-1">
                        {formik.errors.Email}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="MobileNo"
                      className="block text-[#5A3A1E] mb-2 font-medium"
                    >
                      Phone*
                    </label>
                    <input
                      id="MobileNo"
                      name="MobileNo"
                      type="number"
                      placeholder="Enter Your Phone Number"
                      className={`w-full px-6 py-4 bg-[#F3EBDD]/30 border border-[#D8C4A8] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#BF5500] transition-all ${formik.errors.MobileNo && formik.touched.MobileNo ? "border-red-500" : ""}`}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values?.MobileNo}
                    />
                    {formik.touched.MobileNo && formik.errors.MobileNo && (
                      <p className="text-red-500 text-sm mt-1">
                        {formik.errors.MobileNo}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-[#5A3A1E] mb-2 font-medium"
                  >
                    Message
                  </label>
                  <textarea
                    name="Message"
                    id="message"
                    className="w-full px-6 py-4 bg-[#F3EBDD]/30 border border-[#D8C4A8] rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#BF5500] transition-all resize-none"
                    placeholder="Tell us about your inquiry..."
                    maxLength={500}
                    rows={5}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values?.Message}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!formik.isValid || formik.isSubmitting}
                  className="w-full bg-gradient-to-r from-[#E07A2F] to-[#c96a28] text-white px-8 py-5 rounded-full hover:shadow-2xl transition-all hover:scale-105 font-medium flex items-center justify-center gap-3 text-lg disabled:opacity-60"
                >
                  {formik.isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            <div>
              <div className="bg-[#F3EBDD]/50 rounded-3xl p-8 h-full">
                <h3
                  className="text-2xl mb-6 text-[#5A3A1E]"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Find Us
                </h3>
                <div
                  className="bg-white rounded-2xl overflow-hidden shadow-lg mb-6 h-80"
                  dangerouslySetInnerHTML={{ __html: LocationMap }}
                />
                <div className="mt-8">
                  <h3
                    className="text-xl mb-4 text-[#5A3A1E]"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    Connect With Us
                  </h3>

                  <div className="flex gap-4">
                    {instagram && (
                      <a
                        href={instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3E6B4E] to-[#1F3D2B] hover:from-[#E07A2F] hover:to-[#c96a28] transition-all flex items-center justify-center text-white shadow-lg hover:shadow-2xl hover:scale-110"
                      >
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {facebook && (
                      <a
                        href={facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-[#3E6B4E] to-[#1F3D2B] hover:from-[#E07A2F] hover:to-[#c96a28] transition-all flex items-center justify-center text-white shadow-lg hover:shadow-2xl hover:scale-110"
                      >
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
